import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Calendar, CheckCircle2, FileText, ExternalLink, Euro, Mail, Phone } from 'lucide-react';
import { registrationInfo as fallbackReg } from '../mock';
import { useSiteConfig } from '../context/SiteConfigContext';
import { useSiteImages } from '../context/SiteImagesContext';
import { fetchRegistration } from '../api';
import { usePageMeta } from '../hooks/usePageMeta';
import SchedulePlanning from '../components/SchedulePlanning';

const Inscriptions = () => {
  usePageMeta({ title: 'Inscriptions - GR La Saline', description: 'Inscriptions au club GR La Saline : dates, tarifs, pièces requises et documents à télécharger.' });
  const siteConfig = useSiteConfig();
  const images = useSiteImages();
  const [registrationInfo, setRegistrationInfo] = useState(fallbackReg);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  useEffect(() => {
    fetchRegistration().then((data) => { if (data) setRegistrationInfo(data); }).catch(() => {});
  }, []);

  return (
    <div className="bg-black min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={images.inscriptions_hero}
            alt="Inscriptions"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/60 to-black" />
        </div>
        
        <div className="container mx-auto px-6 relative z-10 text-center">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-block px-4 py-2 bg-fuchsia-500/10 border border-fuchsia-500/30 rounded-full text-fuchsia-300 text-sm font-medium tracking-wider mb-6"
          >
            Rejoignez-nous
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-5xl md:text-7xl font-bold text-white mb-6"
          >
            {(() => {
              const full = registrationInfo.title || 'Inscriptions 2026-2027';
              const m = full.match(/^(.*?)(\s*)(\d{4}[-/]\d{4}|\d{4})(.*)$/);
              if (!m) return full;
              return (<>
                {m[1]}{m[2]}
                <span className="bg-gradient-to-r from-fuchsia-400 to-pink-400 bg-clip-text text-transparent">{m[3]}</span>
                {m[4]}
              </>);
            })()}
          </motion.h1>
        </div>
      </section>

      {/* Main Content */}
      <section className="relative py-24" ref={ref}>
        <div className="container mx-auto px-6">
          {/* Process Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center mb-20"
          >
            <p className="text-xl text-white/70 leading-relaxed mb-8">
              {registrationInfo.process}
            </p>
          </motion.div>

          {/* Important Dates */}
          <div className="mb-24">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-3xl md:text-5xl font-bold text-white text-center mb-16"
            >
              Dates <span className="bg-gradient-to-r from-fuchsia-400 to-pink-400 bg-clip-text text-transparent">Importantes</span>
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {registrationInfo.dates.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -10, scale: 1.02 }}
                  className="relative group p-8 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl hover:border-fuchsia-500/50 transition-all duration-500"
                >
                  <div className="absolute top-6 right-6">
                    <Calendar className="text-fuchsia-400/30" size={40} />
                  </div>
                  
                  <div className="relative z-10">
                    <div className="text-3xl font-bold bg-gradient-to-r from-fuchsia-400 to-pink-400 bg-clip-text text-transparent mb-4">
                      {item.date}
                    </div>
                    <p className="text-white text-lg font-medium">{item.event}</p>
                  </div>

                  <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-500/10 to-pink-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl" />
                </motion.div>
              ))}
            </div>
          </div>

          {/* Pricing */}
          <div className="mb-24">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-3xl md:text-5xl font-bold text-white text-center mb-4"
            >
              <span className="bg-gradient-to-r from-fuchsia-400 to-pink-400 bg-clip-text text-transparent">Tarifs</span>
            </motion.h2>
            <p className="text-white/60 text-center mb-16">Licence FFGym incluse dans le T1</p>

            <div className="max-w-5xl mx-auto w-full overflow-x-auto">
              <table className="w-full min-w-[560px] border border-white/10 rounded-2xl overflow-hidden" data-testid="fees-table">
                <thead className="bg-fuchsia-500/10">
                  <tr>
                    <th className="text-left text-white font-semibold px-4 py-3 text-sm">Fréquence</th>
                    <th className="text-center text-white font-semibold px-4 py-3 text-sm">T1 (avec licence)</th>
                    <th className="text-center text-white font-semibold px-4 py-3 text-sm">T2</th>
                    <th className="text-center text-white font-semibold px-4 py-3 text-sm">T3</th>
                    <th className="text-center text-fuchsia-400 font-bold px-4 py-3 text-sm">Total année</th>
                  </tr>
                </thead>
                <tbody>
                  {(registrationInfo.feesTable || []).map((row, i) => (
                    <tr key={i} className="border-t border-white/10 hover:bg-white/5">
                      <td className="text-white/90 px-4 py-3 font-medium">{row.label}</td>
                      <td className="text-white/70 px-4 py-3 text-center">{row.t1}</td>
                      <td className="text-white/70 px-4 py-3 text-center">{row.t2}</td>
                      <td className="text-white/70 px-4 py-3 text-center">{row.t3}</td>
                      <td className="text-fuchsia-300 px-4 py-3 text-center font-bold">{row.annual}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Requirements */}
          <div className="mb-24">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-3xl md:text-5xl font-bold text-white text-center mb-16"
            >
              Pièces <span className="bg-gradient-to-r from-fuchsia-400 to-pink-400 bg-clip-text text-transparent">Requises</span>
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {registrationInfo.requirements.map((req, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ x: 10 }}
                  className="flex items-start gap-4 p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl hover:border-fuchsia-500/30 transition-all duration-300"
                >
                  <CheckCircle2 className="text-fuchsia-400 flex-shrink-0 mt-1" size={24} />
                  <p className="text-white">{req}</p>
                </motion.div>
              ))}
            </div>

            {registrationInfo.documents && registrationInfo.documents.filter(d => d && d.url).length > 0 && (
              <div className="max-w-4xl mx-auto mt-10" data-testid="inscription-documents">
                <h3 className="text-lg font-semibold text-white/80 mb-4 text-center">Documents à télécharger</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {registrationInfo.documents.filter(d => d && d.url).map((doc, i) => (
                    <a
                      key={i}
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-4 bg-white/5 border border-white/10 rounded-xl hover:border-fuchsia-500/40 hover:bg-white/10 transition-all"
                    >
                      <FileText className="text-fuchsia-400 flex-shrink-0" size={20} />
                      <span className="text-white/80 text-sm">{doc.label || 'Document'}</span>
                      <ExternalLink size={14} className="text-white/40 ml-auto" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Weekly Planning */}
          <div className="max-w-6xl mx-auto mt-16">
            <SchedulePlanning type="loisir" seasonTitle={registrationInfo.title} />
            <SchedulePlanning type="competition" seasonTitle={registrationInfo.title} />
          </div>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center"
          >
            <div className="max-w-3xl mx-auto mb-12">
              <h3 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Prêt à <span className="bg-gradient-to-r from-fuchsia-400 to-pink-400 bg-clip-text text-transparent">Commencer ?</span>
              </h3>
              <p className="text-white/70 text-lg mb-8">
                Cliquez sur le bouton ci-dessous pour accéder à notre plateforme d'inscription sécurisée
              </p>
              
              <motion.a
                href={siteConfig.socialLinks.monClub}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-fuchsia-500 to-pink-500 rounded-full text-white font-bold text-lg shadow-lg shadow-fuchsia-500/50 hover:shadow-fuchsia-500/70 transition-all duration-300"
              >
                S'inscrire sur Mon Club
                <ExternalLink size={20} />
              </motion.a>
            </div>

            {/* Contact Info */}
            <div className="max-w-2xl mx-auto p-8 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl">
              <h4 className="text-xl font-bold text-white mb-6">Questions ? Contactez-nous</h4>
              <div className="flex flex-col md:flex-row justify-center gap-8">
                <div className="flex items-center justify-center gap-3 text-white/70">
                  <Phone size={20} className="text-fuchsia-400" />
                  <a href={`tel:${siteConfig.contact.phone}`} className="hover:text-fuchsia-400 transition-colors">
                    {siteConfig.contact.phone}
                  </a>
                </div>
                <div className="flex items-center justify-center gap-3 text-white/70">
                  <Mail size={20} className="text-fuchsia-400" />
                  <a href={`mailto:${siteConfig.contact.email}`} className="hover:text-fuchsia-400 transition-colors">
                    {siteConfig.contact.email}
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Inscriptions;
